/**
 * ============================================================
 * KEJJA OS — src/pages/settings/MembersPage.tsx (FIXED)
 * Uses family-role enum (owner/parent/spouse/child/guardian/guest).
 * ============================================================
 */

import { useState, type FormEvent } from 'react';
import {
  User, UserPlus, Crown, Copy, Check, Trash2,
  ChevronDown, DoorOpen, X, KeyRound, Clock,
} from 'lucide-react';
import { useSettingsContext } from './SettingsLayout';
import {
  useMembers, useInvites, useCreateInvite, useRevokeInvite,
  useChangeRole, useRemoveMember, useLeaveHome, useAcceptInvite,
} from '@/hooks/useSettings';
import type { HomeMember } from '@/lib/settingsApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// Family-role enum values from the DB
type HomeRole = 'owner' | 'parent' | 'spouse' | 'child' | 'guardian' | 'guest';

const ROLES: Array<{ value: HomeRole; label: string; emoji: string; isAdmin: boolean }> = [
  { value: 'owner',    label: 'Owner',    emoji: '👑', isAdmin: true },
  { value: 'parent',   label: 'Parent',   emoji: '🧑‍🍼', isAdmin: true },
  { value: 'spouse',   label: 'Spouse',   emoji: '💍', isAdmin: false },
  { value: 'child',    label: 'Child',    emoji: '🧒', isAdmin: false },
  { value: 'guardian', label: 'Guardian', emoji: '🛡️', isAdmin: false },
  { value: 'guest',    label: 'Guest',    emoji: '🚪', isAdmin: false },
];

const ADMIN_ROLES: readonly string[] = ['owner', 'parent'];

export default function MembersPage() {
  const { homeId, myRole } = useSettingsContext();
  const { data: membersData, isLoading: membersLoading } = useMembers(homeId);
  const isAdmin = ADMIN_ROLES.includes(myRole ?? '');
  const { data: invitesData } = useInvites(homeId, isAdmin);

  const leaveMut = useLeaveHome(homeId);

  const members = membersData?.members ?? [];
  const invites = invitesData?.invites ?? [];

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);

  const handleLeave = () => {
    if (!confirm(`Leave this home? You'll need a new invite to rejoin.`)) return;
    leaveMut.mutate(undefined, {
      onSuccess: () => { window.location.href = '/'; },
      onError: (e) => alert(e.message ?? 'Could not leave'),
    });
  };

  return (
    <div className="space-y-4 pb-12">
      <div className="flex gap-2">
        {isAdmin && (
          <Button
            size="sm" className="bg-sky-600 hover:bg-sky-700 text-white flex-1"
            onClick={() => setShowInviteForm(true)}
          >
            <UserPlus className="w-4 h-4 mr-1" /> Invite member
          </Button>
        )}
        <Button
          size="sm" variant="outline"
          onClick={() => setShowJoinForm(true)}
        >
          <KeyRound className="w-4 h-4 mr-1" /> Join by code
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {membersLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-10" /><Skeleton className="h-10" />
            </div>
          ) : (
            members.map((m) => (
              <MemberRow
                key={m.userId}
                member={m}
                isAdmin={isAdmin}
                homeId={homeId}
              />
            ))
          )}
        </CardContent>
      </Card>

      {isAdmin && invites.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Active invites ({invites.length})
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {invites.map((inv) => (
                <InviteRow key={inv.id} invite={inv} homeId={homeId} />
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="pt-6 border-t border-border mt-8">
        <Button
          variant="ghost" size="sm"
          onClick={handleLeave}
          disabled={leaveMut.isPending}
          className="text-muted-foreground hover:text-rose-600 text-xs"
        >
          <DoorOpen className="w-3 h-3 mr-1" />
          Leave this home
        </Button>
      </div>

      {showInviteForm && (
        <InviteForm homeId={homeId} onClose={() => setShowInviteForm(false)} />
      )}
      {showJoinForm && (
        <JoinForm onClose={() => setShowJoinForm(false)} />
      )}
    </div>
  );
}

function MemberRow({ member, isAdmin, homeId }: {
  member: HomeMember; isAdmin: boolean; homeId: string;
}) {
  const changeRoleMut = useChangeRole(homeId);
  const removeMut = useRemoveMember(homeId);
  const [showRoles, setShowRoles] = useState(false);

  const displayName = member.displayName || member.displayNickname || member.name || 'Member';
  const roleDef = ROLES.find((r) => r.value === member.role);
  const RoleIcon = roleDef?.isAdmin ? Crown : User;

  return (
    <div className="p-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-full bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 flex items-center justify-center flex-shrink-0 text-lg">
        {member.avatarEmoji ?? <User className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm truncate">{displayName}</span>
          <Badge variant="outline" className="text-[9px] h-4 px-1 capitalize">
            <RoleIcon className="w-2 h-2 mr-0.5" />{roleDef ? `${roleDef.emoji} ${roleDef.label}` : member.role}
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground truncate">{member.email ?? '—'}</p>
      </div>
      {isAdmin && (
        <div className="flex items-center gap-1 relative">
          <button
            onClick={() => setShowRoles(!showRoles)}
            className="text-[10px] text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted"
          >
            <ChevronDown className="w-3 h-3 inline mr-0.5" /> Role
          </button>
          {showRoles && (
            <div className="absolute right-10 top-full mt-1 z-10 bg-popover border border-border rounded-md shadow-lg p-1 min-w-[140px]">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => {
                    changeRoleMut.mutate({ userId: member.userId, role: r.value });
                    setShowRoles(false);
                  }}
                  className={cn(
                    'w-full text-left text-xs px-2 py-1 rounded hover:bg-muted flex items-center gap-1.5',
                    member.role === r.value && 'bg-muted',
                  )}
                >
                  <span>{r.emoji}</span><span>{r.label}</span>
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => {
              if (confirm(`Remove ${displayName} from this home?`)) {
                removeMut.mutate(member.userId);
              }
            }}
            className="text-muted-foreground hover:text-rose-600 p-1"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function InviteRow({ invite, homeId }: {
  invite: { id: string; inviteCode: string; role: string; maxUses: number; useCount: number; expiresAt: string | null; note: string | null };
  homeId: string;
}) {
  const revokeMut = useRevokeInvite(homeId);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(invite.inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const expires = invite.expiresAt ? new Date(invite.expiresAt) : null;
  const isExpired = expires && expires < new Date();
  const roleDef = ROLES.find((r) => r.value === invite.role);

  return (
    <div className="p-3 flex items-center gap-3">
      <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
        <KeyRound className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted font-mono text-xs tracking-wider uppercase hover:bg-muted/70"
        >
          {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
          {invite.inviteCode}
        </button>
        <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
          <span>{roleDef ? `${roleDef.emoji} ${roleDef.label}` : invite.role}</span>
          <span>· {invite.useCount}/{invite.maxUses} used</span>
          {expires && (
            <span className={cn('flex items-center gap-0.5', isExpired && 'text-rose-600')}>
              <Clock className="w-2 h-2" />
              {isExpired ? 'Expired' : `Expires ${expires.toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })}`}
            </span>
          )}
          {invite.note && <span className="truncate italic">· "{invite.note}"</span>}
        </div>
      </div>
      <button
        onClick={() => {
          if (confirm('Revoke this invite?')) revokeMut.mutate(invite.id);
        }}
        className="text-muted-foreground hover:text-rose-600"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function InviteForm({ homeId, onClose }: { homeId: string; onClose: () => void }) {
  const createMut = useCreateInvite(homeId);
  const [role, setRole] = useState<HomeRole>('guest');
  const [note, setNote] = useState('');
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInDays, setExpiresInDays] = useState<number | null>(7);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    createMut.mutate({
      role,
      note: note.trim() || null,
      maxUses,
      expiresInDays,
    }, {
      onSuccess: (r) => setCreatedCode(r.invite.inviteCode),
    });
  };

  const copy = async () => {
    if (!createdCode) return;
    await navigator.clipboard.writeText(createdCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-md my-16">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Invite member</h2>
            <Button type="button" size="sm" variant="ghost" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {createdCode ? (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Share this code with whoever you want to add:
              </div>
              <button
                onClick={copy}
                className="w-full px-3 py-4 rounded-lg bg-muted hover:bg-muted/70 transition-colors flex items-center justify-center gap-2 font-mono text-xl tracking-wider uppercase"
              >
                {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5" />}
                {createdCode}
              </button>
              <p className="text-[11px] text-muted-foreground">
                They visit <span className="font-mono">/settings/members</span> → Join by code → paste this.
              </p>
              <Button onClick={onClose} className="w-full bg-sky-600 hover:bg-sky-700 text-white">
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs">Role</Label>
                <div className="grid grid-cols-3 gap-1.5 mt-1">
                  {ROLES.map((r) => (
                    <button
                      key={r.value} type="button"
                      onClick={() => setRole(r.value)}
                      className={cn(
                        'flex items-center gap-1 px-2 py-2 rounded-md border text-[11px] font-medium transition-colors',
                        role === r.value
                          ? 'border-sky-500/60 bg-sky-500/10 text-sky-700 dark:text-sky-300'
                          : 'border-border text-muted-foreground',
                      )}
                    >
                      <span>{r.emoji}</span><span>{r.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  👑 Owner + 🧑‍🍼 Parent can manage the home. Others are members.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Max uses</Label>
                  <Input
                    type="number" min="1" max="100"
                    value={maxUses}
                    onChange={(e) => setMaxUses(parseInt(e.target.value, 10) || 1)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Expires in (days)</Label>
                  <Input
                    type="number" min="1" max="365"
                    value={expiresInDays ?? ''}
                    onChange={(e) => setExpiresInDays(e.target.value ? parseInt(e.target.value, 10) : null)}
                    placeholder="never"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Mum's phone"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                className="bg-sky-600 hover:bg-sky-700 text-white w-full"
                disabled={createMut.isPending}
              >
                {createMut.isPending ? 'Generating…' : 'Generate invite code'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function JoinForm({ onClose }: { onClose: () => void }) {
  const acceptMut = useAcceptInvite();
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null); setSuccess(null);
    if (!code.trim()) { setErr('Enter a code.'); return; }

    acceptMut.mutate(code.trim().toUpperCase(), {
      onSuccess: (r) => setSuccess(`Joined ${r.homeName}!`),
      onError: (e) => setErr(e.message ?? 'Invalid or expired code'),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-sm my-16">
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Join by code</h2>
              <Button type="button" size="sm" variant="ghost" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {success ? (
              <>
                <div className="text-xs text-emerald-600 flex items-center gap-1 p-3 bg-emerald-500/10 rounded-md">
                  <Check className="w-4 h-4" /> {success}
                </div>
                <Button onClick={() => window.location.reload()} className="w-full bg-sky-600 hover:bg-sky-700 text-white">
                  Reload
                </Button>
              </>
            ) : (
              <>
                <Input
                  autoFocus value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="ABCD1234EF"
                  className="text-center font-mono tracking-wider uppercase"
                  maxLength={16}
                />
                {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}
                <Button
                  type="submit"
                  className="bg-sky-600 hover:bg-sky-700 text-white w-full"
                  disabled={acceptMut.isPending}
                >
                  {acceptMut.isPending ? 'Joining…' : 'Join'}
                </Button>
              </>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
